// @flow
import reject from 'lodash/reject'
import some from 'lodash/some'
import {
  addAll,
  addItem,
  move,
  remove,
  removeAll,
} from 'pages/Slices/packetSlice'
import { useLoadFormsAndDocuments } from 'queries/forms/useLoadForms'
import * as React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Form } from 'types/form'
import type { PdfDocumentApiResponse as Document } from 'types/pdf-document-api-response'
import ErrorLabel from 'ui/atoms/ErrorLabel'

import PublicUrlModal from '@/components/Modal/PublicUrlModal'

import CreateUpdatePacketFormList from './CreateUpdatePacketFormList'
import CreateUpdatePacketItemList from './CreateUpdatePacketItemList'

type FormWithType = { ...Form, type: string }
type DocumentWithType = { ...Document, type: string }
type Resource = FormWithType | DocumentWithType

function PacketBuilder(): React.Node {
  const packet = useSelector((state) => state.packet)
  const [params, setParams] = React.useState({
    page: 1,
    search: '',
    pageSize: 25,
  })

  const { data, isFetching } = useLoadFormsAndDocuments({
    ...params,
  })
  const [hasNext, setHasNext] = React.useState(false)
  const [formsAndDocuments, setFormsAndDocuments] = React.useState<
    Array<Resource>,
  >([])

  React.useEffect(() => {
    setHasNext(Boolean(data?.next))
  }, [data?.next])

  const packetItems = React.useMemo(() => packet?.items || [], [packet?.items])

  const dedupeFormsAndDocuments = React.useCallback(
    (previousFormsAndDocuments: Array<any>) => {
      const items = data?.results || []

      const result = [...previousFormsAndDocuments, ...items, ...packetItems]

      return result.filter(
        (obj, index, self) => index === self.findIndex((o) => o.id === obj.id)
      )
    },
    [data?.results, packetItems]
  )

  React.useEffect(() => {
    setFormsAndDocuments(dedupeFormsAndDocuments)
  }, [dedupeFormsAndDocuments])

  const publicUrl = `${window.location.origin}/f/${packet?.id ?? ''}`

  const resources = React.useMemo(() => {
    return (formsAndDocuments || [])
      .filter((resource) => resource.enabled)
      .sort((a, b) => {
        let nameA = a.name
        if (a?.text) {
          nameA = a.text
        }
        let nameB = b.name
        if (b?.text) {
          nameB = b.text
        }
        return nameA?.localeCompare(nameB)
      })
  }, [formsAndDocuments])

  const [hasUrlModal, setHasUrlModal] = useState(false)

  const availableForms = React.useMemo(
    () =>
      reject(resources, ({ id }) =>
        some(packetItems, (selectedId) => selectedId.id === id)
      ),
    [resources, packetItems]
  )

  const dispatch = useDispatch()

  const onAdd = React.useCallback(
    (resource) => {
      dispatch(addItem(resource))
    },
    [dispatch]
  )

  const onAddAll = React.useCallback(() => {
    dispatch(addAll(resources))
  }, [dispatch, resources])

  const onRemove = React.useCallback(
    (itemId) => {
      dispatch(remove(itemId))
    },
    [dispatch]
  )

  const onRemoveAll = React.useCallback(() => {
    dispatch(removeAll())
  }, [dispatch])

  const onMove = React.useCallback(
    (result) => {
      dispatch(
        move({
          sourceIndex: result.source.index,
          destinationIndex: result.destination?.index,
        })
      )
    },
    [dispatch]
  )

  const handleScrollToBottom = React.useCallback(() => {
    if (!hasNext || isFetching) return

    setParams((params) => ({
      ...params,
      page: params.page + 1,
    }))
  }, [hasNext, isFetching])

  return (
    <>
      <div className="w-100 mb-20" style={{ minHeight: '200px' }}>
        <div className="h-100 flex flex-1 flex-shrink-0">
          <CreateUpdatePacketFormList
            forms={availableForms}
            handleScrollToBottom={handleScrollToBottom}
            onAddAll={onAddAll}
            onAddForm={onAdd}
          />

          <CreateUpdatePacketItemList
            forms={packetItems}
            onRemove={onRemove}
            onRemoveAll={onRemoveAll}
            onReorder={onMove}
            packetName={packet?.name}
          />
        </div>
        {packet.errors?.forms && <ErrorLabel>{packet.errors.forms}</ErrorLabel>}
      </div>
      <PublicUrlModal
        formName={packet?.name}
        isVisible={hasUrlModal}
        onClose={() => setHasUrlModal(false)}
        publicUrl={publicUrl}
      />
    </>
  )
}

export default PacketBuilder
